"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Check, Loader2, Search } from "lucide-react";
import { cn, localizePath } from "@repo/lib";
import { Button, Input } from "@repo/ui/controls";
import { useI18n } from "@repo/ui/providers";
import {
  discordThreadUrl,
  VOTABLE_STATUSES,
  type StatsGame,
  type StatsGameWithSummary,
  type StatsStatus,
} from "@/lib/stats-types";
import { formatCount, formatDate } from "./format";
import { CommentBox } from "./game-discussion";
import { STATUS_KEYS, StatusBadge } from "./status-badge";
import { signInUrl, useMyRequests } from "./use-my-requests";
import { VoteButton } from "./vote-button";

const SECTION =
  "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

type SearchResult = {
  appId: number;
  name: string;
  imageUrl: string | null;
  tracked: { id: string; status: StatsStatus } | null;
};

async function postJson<T>(
  url: string,
  body: unknown,
  method = "POST",
  /** Localized "Request failed ({{status}})" template. */
  failed = "Request failed ({{status}})",
): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok)
    throw new Error(
      json.error ?? failed.replace("{{status}}", String(res.status)),
    );
  return json;
}

function GameThumb({ src }: { src: string | null }) {
  return src ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      width={92}
      height={43}
      loading="lazy"
      className="h-[43px] w-[92px] shrink-0 rounded object-cover"
    />
  ) : (
    <span className="h-[43px] w-[92px] shrink-0 rounded bg-muted" />
  );
}

function RequestForm({
  onDone,
}: {
  onDone: (
    message: string,
    game?: { id: string; title: string; status: string },
  ) => void;
}) {
  const { t, locale } = useI18n();
  const { state: my } = useMyRequests();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState<number | "manual" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      fetch(`/api/stats/search?q=${encodeURIComponent(q)}`, {
        signal: ctrl.signal,
      })
        .then((r) => r.json() as Promise<{ results?: SearchResult[] }>)
        .then((b) => setResults(b.results ?? []))
        .catch(() => undefined)
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query]);

  async function requestSteam(r: SearchResult) {
    setBusy(r.appId);
    setError(null);
    try {
      const res = await postJson<{
        id: string;
        title: string;
        status: string;
        created: boolean;
      }>(
        "/api/stats/requests",
        { steamAppId: r.appId },
        "POST",
        t("www.requests.form.failedStatus"),
      );
      onDone(
        res.created
          ? t("www.requests.form.added", { vars: { game: r.name } })
          : t("www.requests.form.voteCounted", { vars: { game: r.name } }),
        { id: res.id, title: res.title, status: res.status },
      );
      setQuery("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("www.requests.form.failed"),
      );
    } finally {
      setBusy(null);
    }
  }

  async function requestManual(e: React.FormEvent) {
    e.preventDefault();
    setBusy("manual");
    setError(null);
    try {
      await postJson(
        "/api/stats/requests",
        { title, url },
        "POST",
        t("www.requests.form.failedStatus"),
      );
      onDone(t("www.requests.form.manualDone", { vars: { game: title } }));
      setTitle("");
      setUrl("");
      setManual(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("www.requests.form.failed"),
      );
    } finally {
      setBusy(null);
    }
  }

  if (my && !my.signedIn) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border bg-card p-6 text-center">
        <p className="text-muted-foreground">
          {t("www.requests.form.signInText")}
        </p>
        <Button asChild>
          <a href={signInUrl()}>{t("www.account.signIn.patreon")}</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("www.requests.form.searchPlaceholder")}
          // Inline: the Input's own px-3 wins over a pl-* utility here.
          style={{ paddingLeft: "2.25rem" }}
          aria-label={t("www.requests.form.searchAria")}
          disabled={!my}
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {results && (
        <ul className="divide-y rounded-md border">
          {results.length === 0 && (
            <li className="p-3 text-sm text-muted-foreground">
              {t("www.requests.form.noResults")}
            </li>
          )}
          {results.map((r) => (
            <li key={r.appId} className="flex items-center gap-3 p-2">
              <GameThumb src={r.imageUrl} />
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              {r.tracked?.status === "supported" ? (
                <Button asChild size="sm" variant="ghost">
                  <Link href={localizePath(`/stats/${r.tracked.id}`, locale)}>
                    <Check className="mr-1 h-4 w-4" />{" "}
                    {t(STATUS_KEYS.supported)}
                  </Link>
                </Button>
              ) : r.tracked?.status === "declined" ? (
                <span className="text-xs text-muted-foreground">
                  {t(STATUS_KEYS.declined)}
                </span>
              ) : (
                <Button
                  size="sm"
                  onClick={() => requestSteam(r)}
                  disabled={busy !== null}
                >
                  {busy === r.appId && (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  )}
                  {r.tracked
                    ? t("www.requests.form.vote")
                    : t("www.requests.form.request")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {manual ? (
        <form
          onSubmit={requestManual}
          className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("www.requests.form.gameTitle")}
            aria-label={t("www.requests.form.gameTitle")}
            required
            minLength={2}
            maxLength={80}
          />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t("www.requests.form.urlPlaceholder")}
            aria-label={t("www.requests.form.urlAria")}
            type="url"
            required
          />
          <Button type="submit" disabled={busy !== null}>
            {busy === "manual" && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            )}
            {t("www.requests.form.send")}
          </Button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setManual(true)}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          {t("www.requests.form.manualToggle")}
        </button>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function PendingReview({
  games,
  isAdmin,
  onChanged,
}: {
  games: StatsGame[];
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState<string | null>(null);
  if (games.length === 0) return null;
  async function act(id: string, action: "requested" | "declined" | "delete") {
    setBusy(id);
    try {
      if (action === "delete")
        await postJson(
          `/api/stats/games/${encodeURIComponent(id)}`,
          undefined,
          "DELETE",
        );
      else
        await postJson(
          `/api/stats/games/${encodeURIComponent(id)}`,
          { status: action },
          "PATCH",
        );
      onChanged();
    } finally {
      setBusy(null);
    }
  }
  return (
    <section className="space-y-3">
      <h2 className={SECTION}>
        {isAdmin
          ? t("www.requests.pending.admin")
          : t("www.requests.pending.mine")}
      </h2>
      <ul className="divide-y rounded-lg border">
        {games.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center gap-3 p-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{g.title}</div>
              {g.url && (
                <a
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block truncate text-xs text-muted-foreground hover:text-foreground"
                >
                  {g.url}
                </a>
              )}
              <div className="text-xs text-muted-foreground">
                {t("www.requests.pending.requestedOn", {
                  vars: { date: formatDate(g.createdAt, locale) },
                })}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => act(g.id, "requested")}
                  disabled={busy === g.id}
                >
                  {t("www.requests.pending.approve")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => act(g.id, "declined")}
                  disabled={busy === g.id}
                >
                  {t("www.requests.pending.decline")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => act(g.id, "delete")}
                  disabled={busy === g.id}
                >
                  {t("www.requests.pending.delete")}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

const BOARD_TABS: StatsStatus[] = ["requested", "watching", "in_progress"];

export function RequestsBoard({ games }: { games: StatsGameWithSummary[] }) {
  const { t, locale } = useI18n();
  const { state: my, setState, refresh } = useMyRequests();
  const [tab, setTab] = useState<StatsStatus>("requested");
  const [notice, setNotice] = useState<string | null>(null);
  const [justRequested, setJustRequested] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      games
        .filter((g) => g.status === tab)
        .sort(
          (a, b) =>
            b.voteCount - a.voteCount ||
            (b.summary.steamCcu ?? -1) - (a.summary.steamCcu ?? -1),
        ),
    [games, tab],
  );

  function onVoted(id: string, voted: boolean) {
    setState((s) =>
      s
        ? {
            ...s,
            votes: voted ? [...s.votes, id] : s.votes.filter((v) => v !== id),
          }
        : s,
    );
  }

  return (
    <div className="space-y-10">
      <section className="space-y-3">
        <h2 className={SECTION}>{t("www.requests.form.heading")}</h2>
        <RequestForm
          onDone={(message, game) => {
            setNotice(message);
            // Ask for details right after a request, while it's fresh.
            setJustRequested(
              game && VOTABLE_STATUSES.includes(game.status as StatsStatus)
                ? game
                : null,
            );
            void refresh();
          }}
        />
        {notice && (
          <p className="text-sm text-emerald-400" role="status">
            {notice} {t("www.requests.form.noticeDelay")}
          </p>
        )}
        {justRequested && (
          <div className="rounded-lg border bg-card p-4">
            <CommentBox
              key={justRequested.id}
              gameId={justRequested.id}
              title={justRequested.title}
              autoFocus
            />
          </div>
        )}
      </section>

      {my && my.pending.length > 0 && (
        <PendingReview
          games={my.pending}
          isAdmin={my.isAdmin}
          onChanged={() => void refresh()}
        />
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {BOARD_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTab(s)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs transition-colors",
                tab === s
                  ? "border-primary bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(STATUS_KEYS[s])}{" "}
              <span className="opacity-70">
                {games.filter((g) => g.status === s).length}
              </span>
            </button>
          ))}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            {tab === "requested"
              ? t("www.requests.board.emptyRequested")
              : t("www.requests.board.empty")}
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {rows.map((g) => (
              <li key={g.id} className="flex items-center gap-3 p-3">
                <VoteButton
                  gameId={g.id}
                  initialCount={g.voteCount}
                  my={my}
                  onVoted={onVoted}
                  onError={setError}
                  size="sm"
                />
                <Link
                  href={localizePath(`/stats/${g.id}`, locale)}
                  className="flex min-w-0 flex-1 items-center gap-3 hover:text-primary"
                >
                  <span className="max-sm:hidden">
                    <GameThumb src={g.imageUrl} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {g.title}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {g.releaseDate
                        ? t("www.stats.release", {
                            vars: { date: g.releaseDate },
                          })
                        : g.steamAppId
                          ? "Steam"
                          : t("www.stats.notOnSteam")}
                    </span>
                  </span>
                </Link>
                <div className="text-right text-sm max-sm:hidden">
                  <div className="tabular-nums">
                    {formatCount(g.summary.steamCcu, locale)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("www.requests.board.playingNow")}
                  </div>
                </div>
                <div className="text-right text-sm max-md:hidden">
                  <div className="tabular-nums">
                    {formatCount(g.summary.steamFollowers, locale)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("www.requests.board.followers")}
                  </div>
                </div>
                {g.discordThreadId && (
                  <a
                    href={discordThreadUrl(g.discordThreadId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground max-md:hidden"
                    title={t("www.requests.board.discussOnDiscord", {
                      vars: { game: g.title },
                    })}
                  >
                    Discord
                  </a>
                )}
                <span className="max-lg:hidden">
                  <StatusBadge status={g.status} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
